import React from 'react';
import PropTypes from 'prop-types';

import { useSelector } from 'react-redux';

import { CreditCardOutlined } from '@ant-design/icons';
import { Col, Row, VertIcon, VertImage, VertTypography } from '@vertem-core/vertice-ds';

import 'Assets/styles/default/components/Orders/OrderDetails/OrderPayment/styles.less';

import { pointsMask } from 'Utils/commons/points';
import { currencyMask } from 'Utils/commons/cash';
import { getOrderStatus } from 'Utils/orders';

const OrderPayment = ({ order }) => {
  const {
    orderPaymentDetailMoneyAndPoints: {
      numberParcelsDecimal,
      imageUrlCardBrand,
      brandName,
      lastCreditCardDigits,
    },
  } = order;

  const hasFreeItems = order.totalItems === 0;
  const hasFreeShipping = order.orderTotalShippingValue === 0;
  const hasDiscount = order.orderPromoCodeValue > 0;
  const hasFreeOrder = order.orderTotalValue === 0;
  const isAddOnCard = order.orderPaymentDetailMoneyAndPoints.purchaseValueDecimal !== 0;

  const orderValueInCash =
    isAddOnCard && currencyMask(order.orderPaymentDetailMoneyAndPoints.purchaseValueDecimal);

  const {
    currencyConfiguration: { decimalPlaces, pointsName },
  } = useSelector(({ Catalog }) => Catalog);

  const formattedTotalItems = pointsMask(order.totalItems, decimalPlaces, pointsName).value;

  const formattedTotalShipping = pointsMask(
    order.orderTotalShippingValue,
    decimalPlaces,
    pointsName,
  ).value;

  const formattedSubtotal = pointsMask(
    order.totalItems + order.orderTotalShippingValue,
    decimalPlaces,
    pointsName,
  ).value;

  const formattedTotalDiscount = pointsMask(
    order.orderPromoCodeValue,
    decimalPlaces,
    pointsName,
  ).value;

  const formattedTotalOrder = pointsMask(order.orderTotalValue, decimalPlaces, pointsName).value;

  const formattedPointsAddOnCardOrder = pointsMask(
    order.orderPaymentDetailMoneyAndPoints.pointsUsedDecimal,
    decimalPlaces,
    pointsName,
  ).value;

  return (
    <Row className='order-payment-section'>
      <Col span={24}>
        <VertTypography.Title className='section-title'>
          <VertIcon component={CreditCardOutlined} />
          Pagamento
        </VertTypography.Title>

        <Row className='container'>
          <Col className='card-related'>
            <VertTypography.Title level={2}>
              {isAddOnCard ? 'Pagamento no cartão' : `Pagamento em ${pointsName}:`}
            </VertTypography.Title>

            {isAddOnCard && (
              <>
                <VertTypography.Paragraph type='light'>
                  {`${numberParcelsDecimal} parcela(s) de `}
                  <span className='payment-info'>{orderValueInCash}</span>
                </VertTypography.Paragraph>

                <div className='card-infos'>
                  <div className='card-brand'>
                    <VertImage alt={brandName} src={imageUrlCardBrand} />
                  </div>

                  <VertTypography.Paragraph type='light'>
                    {`${brandName} final `}
                    <span className='payment-info'>{`${lastCreditCardDigits}`}</span>
                  </VertTypography.Paragraph>
                </div>
              </>
            )}

            <VertTypography.Paragraph className='status approved'>
              {`Pagamento ${getOrderStatus(order.status).label}`}
            </VertTypography.Paragraph>
          </Col>

          <Col className='points-related'>
            <VertTypography.Title level={2}>Resumo do pedido:</VertTypography.Title>

            <>
              <VertTypography.Paragraph className='between' type='light'>
                Total dos itens:{' '}
                {hasFreeItems ? (
                  <span className='payment-info free'>Grátis</span>
                ) : (
                  <span className='payment-info'>{`${formattedTotalItems} ${pointsName}`}</span>
                )}
              </VertTypography.Paragraph>

              <VertTypography.Paragraph className='between' type='light'>
                Total do frete:{' '}
                {hasFreeShipping ? (
                  <span className='payment-info free'>Grátis</span>
                ) : (
                  <span className='payment-info'>{`${formattedTotalShipping} ${pointsName}`}</span>
                )}
              </VertTypography.Paragraph>

              <VertTypography.Paragraph className='between' type='light'>
                Subtotal:{' '}
                {hasFreeItems && hasFreeShipping ? (
                  <span className='payment-info free'>Grátis</span>
                ) : (
                  <span className='payment-info'>{`${formattedSubtotal} ${pointsName}`}</span>
                )}
              </VertTypography.Paragraph>

              {hasDiscount && (
                <VertTypography.Paragraph className='between' type='light'>
                  Desconto:{' '}
                  <span className='payment-info revenue'>{`${formattedTotalDiscount} ${pointsName}`}</span>
                </VertTypography.Paragraph>
              )}

              <div className='final-values'>
                <VertTypography.Paragraph className='between bold' type='light'>
                  Total do resgate:
                  {hasFreeOrder ? (
                    <span className='payment-info free'>Grátis</span>
                  ) : (
                    <span className='payment-info'>{`${formattedTotalOrder} ${pointsName}`}</span>
                  )}
                </VertTypography.Paragraph>
              </div>

              {isAddOnCard && (
                <div className='final-values'>
                  <VertTypography.Paragraph className='between bold' type='light'>
                    Forma de pagamento:
                    <div className='payment-info'>
                      <div className='add-on'>
                        <span>{`${formattedPointsAddOnCardOrder} ${pointsName}`}</span>
                        {isAddOnCard && <span>{`+ ${orderValueInCash} no cartão`}</span>}
                      </div>
                    </div>
                  </VertTypography.Paragraph>
                </div>
              )}
            </>
          </Col>
        </Row>
      </Col>
    </Row>
  );
};

OrderPayment.propTypes = {
  order: PropTypes.array.isRequired,
};

export default OrderPayment;
